import { createFileRoute, notFound, redirect, useRouter } from "@tanstack/react-router";
import { MonthPage } from "@/components/pages/MonthPage";
import { DatePage } from "@/components/pages/DatePage";
import { SiteShell } from "@/components/layout/SiteShell";
import {
  parseYearSlug,
  parseBareMonthSlug,
  parseBareDateSlug,
  parseBareMonthSlugEn,
  parseBareDateSlugEn,
  MONTH_SLUGS_EN_LIST,
  daysInMonth,
} from "@/lib/calendar/dates";
import { withSlash } from "@/lib/url";

const MONTH_NAMES_EN = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];

type Resolved =
  | { kind: "month"; y: number; m: number }
  | { kind: "date"; y: number; m: number; d: number };

export const Route = createFileRoute("/en/calendar/$year/$slug")({
  loader: ({ params }) => {
    const y = parseYearSlug(params.year);
    if (y === null) throw notFound();

    // 1. Prefer EN parsers — if the slug is already canonical EN, render.
    //    This avoids a redirect loop for months whose tokens collide
    //    between locales (april, september, october, november, december).
    const enMo = parseBareMonthSlugEn(params.slug);
    if (enMo) return { kind: "month", y, m: enMo } as Resolved;
    const enDt = parseBareDateSlugEn(params.slug);
    if (enDt) {
      if (enDt.d > daysInMonth(y, enDt.m)) throw notFound();
      return { kind: "date", y, m: enDt.m, d: enDt.d } as Resolved;
    }

    // 2. Otherwise, accept legacy ID slug and 308 to canonical EN — but
    //    only if the target actually differs from the input.
    const idMo = parseBareMonthSlug(params.slug);
    if (idMo) {
      const target = MONTH_SLUGS_EN_LIST[idMo - 1];
      if (target !== params.slug) {
        throw redirect({ to: `/en/calendar/${y}/${target}/`, code: 308 } as any);
      }
    }
    const idDt = parseBareDateSlug(params.slug);
    if (idDt) {
      const target = `${idDt.d}-${MONTH_SLUGS_EN_LIST[idDt.m - 1]}`;
      if (target !== params.slug) {
        throw redirect({ to: `/en/calendar/${y}/${target}/`, code: 308 } as any);
      }
    }
    throw notFound();
  },
  head: ({ loaderData, params }) => {
    if (!loaderData) return {};
    let title = "Indonesia Calendar";
    let desc = "Complete Indonesia calendar.";
    if (loaderData.kind === "month") {
      const mn = MONTH_NAMES_EN[loaderData.m - 1];
      title = `${mn} ${loaderData.y} Calendar - Holidays, Hijri & Javanese`;
      desc = `${mn} ${loaderData.y}: public holidays, joint leave, Hijri, Javanese weton, Balinese.`;
    } else {
      const mn = MONTH_NAMES_EN[loaderData.m - 1];
      title = `${loaderData.d} ${mn} ${loaderData.y} - What Day, Weton & Hijri`;
      desc = `Date detail ${loaderData.d} ${mn} ${loaderData.y}: weekday, Javanese weton, Hijri, Balinese, holiday status.`;
    }
    const canonical = `https://hari.co.id${withSlash(`/en/calendar/${params.year}/${params.slug}`)}`;
    return {
      meta: [
        { title },
        { name: "description", content: desc },
        { property: "og:title", content: title },
        { property: "og:description", content: desc },
        { property: "og:url", content: canonical },
        { property: "og:locale", content: "en_US" },
        { property: "og:locale:alternate", content: "id_ID" },
      ],
      links: [{ rel: "canonical", href: canonical }],
    };
  },
  component: SlugPage,
  notFoundComponent: () => (
    <SiteShell locale="en">
      <div className="py-16 text-center"><h1 className="text-2xl font-bold">Page not found</h1></div>
    </SiteShell>
  ),
  errorComponent: ({ error, reset }) => {
    const router = useRouter();
    return (
      <SiteShell locale="en">
        <div className="py-16 text-center">
          <h1 className="text-2xl font-bold">Something went wrong</h1>
          <p className="mt-2 text-sm text-muted-foreground">{error.message}</p>
          <button onClick={() => { router.invalidate(); reset(); }} className="mt-4 rounded-md bg-primary px-4 py-2 text-sm text-primary-foreground">
            Retry
          </button>
        </div>
      </SiteShell>
    );
  },
});

function SlugPage() {
  const data = Route.useLoaderData();
  if (data.kind === "month") return <MonthPage y={data.y} m={data.m} locale="en" />;
  return <DatePage y={data.y} m={data.m} d={data.d} locale="en" />;
}